import {
	Box,
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	useDisclosure,
	Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';

import useStore from '../../../store';
import c from '../../../constant';
import SelectFormField from '../../form/SelectFormField';

const validationSchema = Yup.object().shape({
	department: Yup.string().nullable(),
	office: Yup.string().nullable(),
});

export default function OfficialProfileFilter({ department = null, office = null, onCallback = () => null }) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();

	const [selectedDepartment, setSelectedDepartment] = useState(department || '');
	const [selectedOffice, setSelectedOffice] = useState(office || '');

	useEffect(() => {
		setSelectedOffice('');
	}, [selectedDepartment]);

	useEffect(() => {
		if (isOpen) {
			setSelectedDepartment(department || '');
			setSelectedOffice(office || '');
		}
	}, [isOpen, department, office]);

	return (
		<>
			<Button
				onClick={onOpen}
				leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
				colorScheme={(department && department !== 'ALL') || (office && office !== 'ALL') ? 'yellow' : 'gray'}
				variant="solid"
				size="sm"
				borderRadius={c.borderRadius}
				border="1px solid #bdc3c7"
			>
				<Text fontSize="sm" fontWeight="semibold">
					{t('Filter')}
				</Text>
			</Button>

			<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Filter')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Formik
							enableReinitialize
							validationSchema={validationSchema}
							initialValues={{
								department: selectedDepartment,
								office: selectedOffice,
							}}
							onSubmit={(values) => {
								let url = '?page=1';

								const selectedDept = values?.department;
								const selectedOffice = values?.office;

								const validOffices = c.departments?.find((d) => d?.value === selectedDept)?.offices?.map((office) => office?.value) || [];

								url += selectedDept && selectedDept !== 'ALL' ? `&department=${selectedDept}` : '';

								if (selectedOffice && selectedOffice !== 'ALL' && (!selectedDept || validOffices.includes(selectedOffice))) {
									url += `&office=${selectedOffice}`;
								}

								onCallback(url);
								onClose();
							}}
						>
							{({ setFieldValue, values }) => (
								<Box pb="4">
									<Form>
										<SimpleGrid columns={1} spacing={4}>
											<Box w="100%" ml="2">
												<SelectFormField
													name="department"
													label="Department"
													options={[
														{ label: 'ALL', value: 'ALL' },
														...(c?.departments || [])?.map((d) => ({
															label: lang === 'kh' ? d.labelKh : d.label,
															value: d.value,
														})),
													]}
													initialValue={department ? department : ''}
													t={t}
												/>
												<SelectFormField
													name="office"
													label="Office"
													options={[
														{ label: 'ALL', value: 'ALL' },
														...(c.departments?.find((d) => d?.value === values?.department)?.offices || [])?.map((office) => ({
															label: lang === 'kh' ? office?.labelKh : office?.label,
															value: office?.value,
														})),
													]}
													initialValue={office ? office : ''}
													t={t}
												/>
											</Box>

											<Flex mt={2} justifyContent="flex-end">
												<Button
													mr={2}
													colorScheme="gray"
													variant="solid"
													size="md"
													h="42px"
													w="128px"
													borderRadius={c.borderRadius}
													onClick={() => {
														setSelectedDepartment('');
														setSelectedOffice('');
														onCallback('?page=1');
														onClose();
													}}
												>
													{t('Clear')}
												</Button>
												<Button
													ml={2}
													colorScheme="blue"
													variant="solid"
													size="md"
													h="42px"
													w="128px"
													type="submit"
													borderRadius={c.borderRadius}
												>
													{t('Apply')}
												</Button>
											</Flex>
										</SimpleGrid>
									</Form>
								</Box>
							)}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}
